import { useState } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import type {
  CreditActiveLayer,
  CreditApplicationStatus,
  CreditLayerUsage,
  GetTemporaryCreditApplicationsResponse,
} from '@nadoutong/contracts';
import { Plus } from 'lucide-react';
import {
  useCreditApplications,
  useCreditOverview,
  useCreditOverdue,
  useRevokeCreditExemption,
  useTemporaryCreditApplications,
} from '../api/credit.ts';
import { CreditBillingTimeline } from './credit/CreditBillingTimeline.tsx';
import {
  archivedCapCnyCents,
  resolveCreditContractReadback,
} from './credit/contract-readback.ts';
import { CreditLayerUsageBar } from './credit/CreditLayerUsageBar.tsx';
import { CreditMailDeliveriesPanel } from './credit/CreditMailDeliveriesPanel.tsx';
import { hasExplicitOpsRole, ROLE_OPS_SUPER_ADMIN } from '../auth/opsRoles.ts';
import { useSession } from '../hooks/useSession.tsx';
import { CreditContractDownload } from './CreditContractDownload.tsx';
import { CreditExemptionDialog } from './CreditExemptionDialog.tsx';
import { CustomerCreditApplyDialog } from './CustomerCreditApplyDialog.tsx';
import { TemporaryCreditApplyDialog } from './TemporaryCreditApplyDialog.tsx';
import { TemporaryCreditEvidenceDownload } from './TemporaryCreditEvidenceDownload.tsx';
import { DualCurrencyAmount } from './DualCurrencyAmount.tsx';
import { OpsUserRef } from './identity/OpsUserRef.tsx';
import {
  formatCnyCents,
  formatDisplayRateHint,
  formatLocalTimestamp,
  usdMicrosToCnyCents,
} from './formatters.ts';
import { usePricing } from '../api/pricing.ts';
import { coerceErrorMessage, useToast } from './Toast.tsx';
import { useT } from '../i18n/index.ts';
import type { I18nKey, TParams } from '../i18n/index.ts';
import {
  Button,
  Chip,
  ConfirmDialog,
  EmptyState,
  KpiCard,
  Panel,
  StatusLamp,
  Table,
  TableEmpty,
  TableSkeleton,
  TBody,
  Td,
  Th,
  THead,
  Tr,
} from '../ui/index.ts';
import type { ChipTone, LampTone } from '../ui/index.ts';

/**
 * Customer 360 credit section (epic #1033 E4-C1, #1125; router#1999,
 * router#2061): the customer's current credit line from the overview
 * layers (a missing grant is prepaid, not "inherit the platform default
 * limit"), payment terms when a grant exists, the apply entry point
 * (submit never activates the line), and the application history.
 * Amounts are integer micro-USD on the wire; this screen formats CNY
 * only (router#2085). The locked-rate caption is the sole USD remainder.
 */

const STATUS_LABEL_KEYS: Record<CreditApplicationStatus, I18nKey> = {
  pending_approval: 'credit.status.pending_approval',
  approved: 'credit.status.approved',
  rejected: 'credit.status.rejected',
};

const OVERDUE_STAGE_LABEL_KEYS: Record<
  'frozen' | 'stop_notice' | 'stopped' | 'collections',
  I18nKey
> = {
  frozen: 'credit.overdue.stage.frozen',
  stop_notice: 'credit.overdue.stage.stop_notice',
  stopped: 'credit.overdue.stage.stopped',
  collections: 'credit.overdue.stage.collections',
};

const STATUS_TONE: Record<CreditApplicationStatus, ChipTone> = {
  pending_approval: 'warn',
  approved: 'ok',
  rejected: 'bad',
};

const LAYER_LAMP: Record<CreditActiveLayer, LampTone> = {
  prepaid: 'ok',
  long_term: 'warn',
  temporary: 'warn',
  over_limit: 'bad',
};

function lockedRateCaption(
  t: (key: I18nKey, params?: TParams) => string,
  appliedRate: number | null | undefined,
): JSX.Element | null {
  if (appliedRate === null || appliedRate === undefined) return null;
  return (
    <span className="text-caption text-paper-dim" data-testid="credit-locked-rate">
      {t('credit.overview.applied_rate', { rate: formatDisplayRateHint(appliedRate) })}
    </span>
  );
}

function formatLayerCny(usdMicros: number, cnyPerUsd: number | null): string {
  const cnyCents = usdMicrosToCnyCents(usdMicros, cnyPerUsd);
  return cnyCents === null ? '—' : formatCnyCents(cnyCents, false);
}

function layerStatusText(
  t: (key: I18nKey, params?: TParams) => string,
  layers: CreditLayerUsage,
  cnyPerUsd: number | null,
): string {
  const debt = formatLayerCny(Math.max(0, -layers.book_balance_usd_micros), cnyPerUsd);
  if (layers.active_layer === 'over_limit') {
    return t('credit.overview.status.over_limit', {
      over: formatLayerCny(layers.over_limit_usd_micros, cnyPerUsd),
      debt,
    });
  }
  if (layers.active_layer === 'temporary') {
    return t('credit.overview.status.temporary', {
      used: formatLayerCny(layers.temporary_used_usd_micros, cnyPerUsd),
      cap: formatLayerCny(layers.temporary_cap_usd_micros, cnyPerUsd),
      debt,
    });
  }
  if (layers.active_layer === 'long_term') {
    return t('credit.overview.status.long_term', {
      used: formatLayerCny(layers.long_term_used_usd_micros, cnyPerUsd),
      cap: formatLayerCny(layers.long_term_cap_usd_micros, cnyPerUsd),
      debt,
    });
  }
  return t('credit.overview.status.prepaid', {
    amount: formatLayerCny(layers.prepaid_remaining_usd_micros, cnyPerUsd),
  });
}

interface CustomerCreditSectionProps {
  readonly orgId: string;
  readonly customerEmail: string;
  /**
   * #1958: the finance manager reads this section to review the credit
   * application they are about to sign as stage 2 — so the limits and the
   * history stay, and the apply entry point (which posts the contract
   * attachment and the application, both refused for that session) goes.
   */
  readonly canWrite: boolean;
}

export function CustomerCreditSection({
  orgId,
  customerEmail,
  canWrite,
}: CustomerCreditSectionProps): JSX.Element {
  const t = useT();
  const { user } = useSession();
  const pricing = usePricing();
  const cnyPerUsd = pricing.data?.policy?.cny_per_usd ?? null;
  const canExempt = hasExplicitOpsRole(user?.roles ?? [], ROLE_OPS_SUPER_ADMIN);
  const [applyOpen, setApplyOpen] = useState(false);
  const [temporaryApplyOpen, setTemporaryApplyOpen] = useState(false);
  const [exemptionOpen, setExemptionOpen] = useState(false);
  const [exemptionRevokeOpen, setExemptionRevokeOpen] = useState(false);
  const query = useCreditApplications(orgId, true);
  const overviewQuery = useCreditOverview(orgId, true);
  const overdueQuery = useCreditOverdue(orgId, true);
  const temporaryApplicationsQuery = useTemporaryCreditApplications(orgId, true);
  const data = query.data ?? null;
  const overview = overviewQuery.data ?? null;
  const ownerAccountId = overview?.account_id ?? null;
  const overdue = overdueQuery.data?.account ?? null;
  const applications = data?.applications ?? [];
  const readback =
    overview !== null && data !== null
      ? resolveCreditContractReadback({
          layers: overview.layers,
          billingGraceDays: data.profile.billing_grace_days,
          effectiveGraceDays: data.effective.grace_days,
        })
      : null;

  return (
    <Panel
      caption={t('credit.section.title')}
      count={data?.applications.length}
      testId="customer-credit-section"
      actions={
        data !== null && (canWrite || canExempt) ? (
          <div className="flex items-center gap-1.5">
            {canExempt ? (
              <>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setExemptionOpen(true)}
                  data-testid="customer-credit-exemption-open"
                >
                  {t('credit.exemption.open')}
                </Button>
                {overdue !== null && overdue.exemption !== null ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setExemptionRevokeOpen(true)}
                    data-testid="customer-credit-exemption-revoke"
                  >
                    {t('credit.exemption.revoke')}
                  </Button>
                ) : null}
              </>
            ) : null}
            {canWrite ? (
              <>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setTemporaryApplyOpen(true)}
                  data-testid="customer-temporary-credit-apply-open"
                >
                  {t('credit.section.temporary_apply')}
                </Button>
                <Button
                  size="sm"
                  variant="primary"
                  onClick={() => setApplyOpen(true)}
                  data-testid="customer-credit-apply-open"
                >
                  <Plus aria-hidden="true" className="h-3.5 w-3.5" />
                  {t('credit.section.apply')}
                </Button>
              </>
            ) : null}
          </div>
        ) : undefined
      }
    >
      {query.isPending || overviewQuery.isPending ? (
        <TableSkeleton rows={3} testId="customer-credit-loading" />
      ) : null}
      {query.isError ? (
        <EmptyState
          title={t('credit.section.load_error')}
          hint={coerceErrorMessage(query.error)}
          action={<Button onClick={() => void query.refetch()}>{t('common.retry')}</Button>}
          testId="customer-credit-error"
        />
      ) : null}
      {overviewQuery.isError ? (
        <EmptyState
          title={t('credit.section.load_error')}
          hint={coerceErrorMessage(overviewQuery.error)}
          action={<Button onClick={() => void overviewQuery.refetch()}>{t('common.retry')}</Button>}
          testId="customer-credit-overview-error"
        />
      ) : null}
      {overview !== null ? (
        <div className="flex flex-col gap-4 p-4" data-testid="customer-credit-overview">
          <p
            className="m-0 flex items-center gap-2 text-body font-medium text-paper"
            data-testid="customer-credit-status"
            data-layer={overview.layers.active_layer}
          >
            <StatusLamp tone={LAYER_LAMP[overview.layers.active_layer]} />
            {layerStatusText(t, overview.layers, cnyPerUsd)}
          </p>
          <CreditLayerUsageBar
            layers={overview.layers}
            cnyPerUsd={cnyPerUsd}
            lockedCapCnyCents={{
              long_term: data?.profile.credit_limit_cny_cents,
              temporary: data?.profile.temporary_limit_cny_cents,
            }}
          />
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <KpiCard
              label={t('credit.overview.kpi.prepaid')}
              value={
                <DualCurrencyAmount
                  display="cny"
                  cnyCents={usdMicrosToCnyCents(
                    overview.layers.prepaid_remaining_usd_micros,
                    cnyPerUsd,
                  )}
                  signed={false}
                  align="start"
                />
              }
              testId="customer-credit-kpi-prepaid"
            />
            <KpiCard
              label={t('credit.overview.kpi.long_term')}
              value={
                <div className="flex flex-col items-start">
                  <DualCurrencyAmount
                    display="cny"
                    cnyCents={
                      data?.profile.credit_limit_cny_cents ??
                      usdMicrosToCnyCents(overview.layers.long_term_cap_usd_micros, cnyPerUsd)
                    }
                    signed={false}
                    align="start"
                  />
                  {lockedRateCaption(t, data?.profile.credit_limit_applied_rate)}
                </div>
              }
              testId="customer-credit-kpi-long-term"
            />
            <KpiCard
              label={t('credit.overview.kpi.temporary')}
              value={
                <div className="flex flex-col items-start">
                  <DualCurrencyAmount
                    display="cny"
                    cnyCents={
                      data?.profile.temporary_limit_cny_cents ??
                      usdMicrosToCnyCents(overview.layers.temporary_cap_usd_micros, cnyPerUsd)
                    }
                    signed={false}
                    align="start"
                  />
                  {lockedRateCaption(t, data?.profile.temporary_limit_applied_rate)}
                </div>
              }
              testId="customer-credit-kpi-temporary"
            />
            <KpiCard
              label={t('credit.overview.kpi.consumption')}
              value={
                <DualCurrencyAmount
                  display="cny"
                  cnyCents={usdMicrosToCnyCents(
                    overview.layers.cumulative_consumption_usd_micros,
                    cnyPerUsd,
                  )}
                  signed={false}
                  align="start"
                />
              }
              testId="customer-credit-kpi-consumption"
            />
          </div>
          <CreditBillingTimeline
            summary={overview.timeline.summary}
            entries={overview.timeline.entries}
            cnyPerUsd={cnyPerUsd}
          />
        </div>
      ) : null}
      {data !== null ? (
        <div className="flex flex-col gap-4 p-4">
          {/*
            One payment-terms card, not two. Before router#1999 the section
            showed a cycle KPI and a grace KPI; both now read the same
            `effective.grace_days`, because 账期 collapsed to one concept —
            days from statement issue to due. Rendering it twice under two
            labels reads as two independent facts on the screen the stage-2
            signer uses. Limit figures come from creditCapUsdMicros of
            overview.layers (router#2061) — the same long-term + temporary
            sum the gateway spends against.
          */}
          {readback !== null ? (
            <div className="grid gap-3 sm:grid-cols-2">
              <KpiCard
                label={t('credit.kpi.limit')}
                value={
                  readback.kind === 'prepaid' ? (
                    t('credit.kpi.none')
                  ) : (
                    <div className="flex flex-col items-start">
                      <DualCurrencyAmount
                        display="cny"
                        cnyCents={
                          archivedCapCnyCents(data.profile) ??
                          usdMicrosToCnyCents(readback.limitUsdMicros, cnyPerUsd)
                        }
                        signed={false}
                        align="start"
                      />
                      {lockedRateCaption(
                        t,
                        data.profile.credit_limit_applied_rate ??
                          data.profile.temporary_limit_applied_rate,
                      )}
                    </div>
                  )
                }
                unit={
                  readback.kind === 'granted' && readback.limitUsdMicros > 0
                    ? t('credit.kpi.custom')
                    : undefined
                }
                testId="customer-credit-kpi-limit"
              />
              <KpiCard
                label={t('credit.kpi.cycle')}
                value={
                  readback.kind === 'prepaid'
                    ? t('credit.kpi.none')
                    : t('credit.kpi.days', { days: String(readback.paymentTermsDays) })
                }
                unit={
                  readback.kind === 'granted'
                    ? readback.paymentTermsInherited
                      ? t('credit.kpi.inherited')
                      : t('credit.kpi.custom')
                    : undefined
                }
                testId="customer-credit-kpi-cycle"
              />
            </div>
          ) : null}

          {/*
            #1957: the contract read-back sits OUTSIDE the `canWrite` branch on
            purpose — the finance manager who signs stage 2 writes nothing here
            and still has to open the contract.
          */}
          {overdue !== null && overdue.exemption !== null ? (
            <Chip tone="warn" testId="customer-credit-exemption-chip">
              {t('credit.exemption.chip', { days: String(overdue.exemption.remaining_days) })}
            </Chip>
          ) : null}
          {overdue !== null && overdue.stage !== null && overdue.stage !== 'cleared' ? (
            <Chip
              tone={overdue.stage === 'stopped' || overdue.stage === 'collections' ? 'bad' : 'warn'}
              testId="customer-credit-overdue-stage"
            >
              {t(OVERDUE_STAGE_LABEL_KEYS[overdue.stage])}
            </Chip>
          ) : null}
          <CreditContractDownload orgId={orgId} testId="customer-credit-contract" />
          <TemporaryCreditEvidenceDownload orgId={orgId} testId="customer-temporary-credit-evidence" />

          <Table testId="customer-credit-table" className="rounded-none border-0">
            <THead>
              <Tr interactive={false}>
                <Th>{t('credit.col.status')}</Th>
                <Th num>{t('credit.col.limit')}</Th>
                <Th num>{t('credit.col.applied_rate')}</Th>
                <Th num>{t('credit.col.cycle')}</Th>
                <Th>{t('credit.col.reason')}</Th>
                <Th>{t('credit.col.requester')}</Th>
                <Th>{t('credit.col.created_at')}</Th>
              </Tr>
            </THead>
            <TBody>
              {applications.length === 0 ? (
                <TableEmpty colSpan={7} testId="customer-credit-empty">
                  {t('credit.section.empty')}
                </TableEmpty>
              ) : (
                applications.map((application) => (
                  <Tr key={application.id} data-testid={`customer-credit-row-${application.id}`}>
                    <Td>
                      <Chip tone={STATUS_TONE[application.status]}>
                        {t(STATUS_LABEL_KEYS[application.status])}
                      </Chip>
                    </Td>
                    <Td num emphasis>
                      <DualCurrencyAmount
                        display="cny"
                        cnyCents={application.requested_limit_cny_cents}
                        signed={false}
                      />
                    </Td>
                    <Td num>
                      <span data-testid={`customer-credit-applied-rate-${application.id}`}>
                        {formatDisplayRateHint(application.applied_rate)}
                      </span>
                    </Td>
                    <Td num>
                      {t('credit.kpi.days', { days: String(application.requested_cycle_days) })}
                    </Td>
                    <Td>
                      <span data-testid={`customer-credit-reason-${application.id}`}>
                        {application.reason === null || application.reason.length === 0
                          ? t('approvals.credit.reason_unset')
                          : application.reason}
                      </span>
                    </Td>
                    <Td mono>
                      <OpsUserRef id={application.requester_ops_user_id} />
                    </Td>
                    <Td mono>{formatLocalTimestamp(application.created_at)}</Td>
                  </Tr>
                ))
              )}
            </TBody>
          </Table>
          <TemporaryApplicationsPanel query={temporaryApplicationsQuery} cnyPerUsd={cnyPerUsd} />
          <div>
            <Link
              to={`/finance/credit-grants?customer=${encodeURIComponent(orgId)}`}
              className="inline-flex items-center text-dense text-signal hover:underline"
              data-testid="customer-credit-history-link"
            >
              {t('credit.section.history_link')}
            </Link>
          </div>
          {ownerAccountId !== null ? (
            <CreditMailDeliveriesPanel orgId={orgId} accountId={ownerAccountId} />
          ) : null}
        </div>
      ) : null}
      {data !== null && canWrite ? (
        <>
          <CustomerCreditApplyDialog
            open={applyOpen}
            onOpenChange={setApplyOpen}
            orgId={orgId}
            customerEmail={customerEmail}
            defaults={data.defaults}
            onSubmitted={() => {
              void query.refetch();
            }}
          />
          <TemporaryCreditApplyDialog
            open={temporaryApplyOpen}
            onOpenChange={setTemporaryApplyOpen}
            orgId={orgId}
            customerEmail={customerEmail}
          />
        </>
      ) : null}
      {canExempt ? (
        <>
          <CreditExemptionDialog
            orgId={orgId}
            customerEmail={customerEmail}
            open={exemptionOpen}
            onOpenChange={setExemptionOpen}
          />
          <ExemptionRevokeDialog
            orgId={orgId}
            open={exemptionRevokeOpen}
            onOpenChange={setExemptionRevokeOpen}
          />
        </>
      ) : null}
    </Panel>
  );
}

function ExemptionRevokeDialog({
  orgId,
  open,
  onOpenChange,
}: {
  readonly orgId: string;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}): JSX.Element {
  const t = useT();
  const toast = useToast();
  const revoke = useRevokeCreditExemption();

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={(next) => {
        if (!revoke.isPending) {
          onOpenChange(next);
        }
      }}
      title={t('credit.exemption.revoke_title')}
      body={t('credit.exemption.revoke_body')}
      confirmLabel={t('credit.exemption.revoke_confirm')}
      cancelLabel={t('common.cancel')}
      closeLabel={t('common.close')}
      tone="danger"
      loading={revoke.isPending}
      testId="credit-exemption-revoke-dialog"
      onConfirm={() => {
        revoke.mutate(orgId, {
          onSuccess: () => {
            toast.success(t('credit.exemption.revoke_toast'));
            onOpenChange(false);
          },
          onError: (error) => {
            toast.error(coerceErrorMessage(error));
          },
        });
      }}
    />
  );
}

function TemporaryApplicationsPanel({
  query,
  cnyPerUsd,
}: {
  readonly query: UseQueryResult<GetTemporaryCreditApplicationsResponse>;
  readonly cnyPerUsd: number | null;
}): JSX.Element {
  const t = useT();
  const applications = query.data?.applications ?? [];

  return (
    <div className="flex flex-col gap-2" data-testid="customer-temporary-credit-applications">
      <h3 className="m-0 text-section text-paper">
        {t('credit.section.temporary_applications.title')}
      </h3>
      {query.isPending ? (
        <TableSkeleton rows={2} testId="customer-temporary-credit-applications-loading" />
      ) : null}
      {query.isError ? (
        <EmptyState
          title={t('credit.section.temporary_applications.load_error')}
          hint={coerceErrorMessage(query.error)}
          action={<Button onClick={() => void query.refetch()}>{t('common.retry')}</Button>}
          testId="customer-temporary-credit-applications-error"
        />
      ) : null}
      {query.data !== undefined ? (
        <Table
          testId="customer-temporary-credit-applications-table"
          className="rounded-none border-0"
        >
          <THead>
            <Tr interactive={false}>
              <Th>{t('credit.section.temporary_applications.col.created_at')}</Th>
              <Th num>{t('credit.section.temporary_applications.col.requested')}</Th>
              <Th>{t('credit.section.temporary_applications.col.expected_arrival')}</Th>
              <Th>{t('credit.section.temporary_applications.col.status')}</Th>
              <Th>{t('credit.section.temporary_applications.col.grant')}</Th>
            </Tr>
          </THead>
          <TBody>
            {applications.length === 0 ? (
              <TableEmpty colSpan={5} testId="customer-temporary-credit-applications-empty">
                {t('credit.section.temporary_applications.empty')}
              </TableEmpty>
            ) : (
              applications.map((application) => {
                const requestedCny =
                  application.requested_limit_cny_cents !== null &&
                  Number.isSafeInteger(application.requested_limit_cny_cents)
                    ? application.requested_limit_cny_cents
                    : usdMicrosToCnyCents(application.requested_limit_usd_micros, cnyPerUsd);
                const expectedCny =
                  application.expected_arrival_amount_cny_cents !== null &&
                  Number.isSafeInteger(application.expected_arrival_amount_cny_cents)
                    ? application.expected_arrival_amount_cny_cents
                    : usdMicrosToCnyCents(
                        application.expected_arrival_amount_usd_micros,
                        cnyPerUsd,
                      );
                return (
                  <Tr
                    key={application.id}
                    data-testid={`customer-temporary-credit-application-${application.id}`}
                  >
                    <Td mono>{formatLocalTimestamp(application.created_at)}</Td>
                    <Td num>
                      <DualCurrencyAmount
                        display="cny"
                        cnyCents={requestedCny}
                        signed={false}
                      />
                    </Td>
                    <Td>
                      <div className="flex flex-col gap-0.5">
                        <span className="font-mono text-micro">{application.expected_arrival_date}</span>
                        <DualCurrencyAmount
                          display="cny"
                          cnyCents={expectedCny}
                          signed={false}
                          align="start"
                        />
                      </div>
                    </Td>
                    <Td>
                      <Chip tone={STATUS_TONE[application.status]}>
                        {t(STATUS_LABEL_KEYS[application.status])}
                      </Chip>
                    </Td>
                    <Td mono>
                      {application.grant_id === null || application.grant_id.length === 0
                        ? t('credit.overview.dash')
                        : application.grant_id}
                    </Td>
                  </Tr>
                );
              })
            )}
          </TBody>
        </Table>
      ) : null}
    </div>
  );
}
